import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Plus, X, Check, Star, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import ShareDialog from "../../components/os/ShareDialog";
import ItemMenu from "../../components/os/ItemMenu";
import { api, formatApiError, uploadImage, mediaUrl } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];
const FALLBACK_IMG = "https://images.unsplash.com/photo-1677761640321-b80251be00ca?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const inputCls = "w-full bg-cream border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors";

export default function AuraProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [p, setP] = useState(null);
  const [collections, setCollections] = useState([]);
  const [lib, setLib] = useState(null);
  const [tab, setTab] = useState("details");
  const [share, setShare] = useState(false);
  const doDelete = async () => {
    if (!window.confirm(`¿Eliminar el producto "${p.name}"? Esta acción no se puede deshacer.`)) return;
    try { await api.delete(`/aura/products/${id}`); toast.success("Producto eliminado"); navigate("/os/aura"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error"); }
  };

  const load = useCallback(() => {
    api.get(`/aura/products/${id}`).then((r) => setP(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => {
    load();
    api.get("/aura/collections").then((r) => setCollections(r.data)).catch(() => {});
    api.get("/aura/library").then((r) => setLib(r.data)).catch(() => {});
  }, [load]);

  const set = (k, v) => setP({ ...p, [k]: v });
  const setSeo = (k, v) => setP({ ...p, seo: { ...(p.seo || {}), [k]: v } });
  const setVariant = (i, k, v) => setP({ ...p, variants: p.variants.map((vr, idx) => (idx === i ? { ...vr, [k]: v } : vr)) });
  const addVariant = () => setP({ ...p, variants: [...(p.variants || []), { id: crypto.randomUUID(), name: "", sku: "", price: p.price, stock: 0 }] });
  const removeVariant = (i) => setP({ ...p, variants: p.variants.filter((_, idx) => idx !== i) });

  const save = async () => {
    try {
      const payload = {
        name: p.name, sku: p.sku, description: p.description, category: p.category,
        collection: p.collection, price: Number(p.price) || 0, status: p.status,
        cover: p.cover, gallery: p.gallery || [],
        variants: (p.variants || []).map((v) => ({ ...v, price: Number(v.price) || 0, stock: Number(v.stock) || 0 })),
        materials: p.materials || [], seo: p.seo || {},
      };
      const { data } = await api.patch(`/aura/products/${id}`, payload);
      setP(data); toast.success(t("os.aura.save"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [genDesc, setGenDesc] = useState(false);
  const [genSeo, setGenSeo] = useState(false);
  const generateDesc = async () => {
    setGenDesc(true);
    try { const { data } = await api.post(`/aura/products/${id}/description/generate`, { lang }); set("description", data.description); toast.success("✨ OMNIA AI"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setGenDesc(false); }
  };
  const generateSeo = async () => {
    setGenSeo(true);
    try { const { data } = await api.post(`/aura/products/${id}/seo/generate`, { lang }); setP((prev) => ({ ...prev, seo: data })); toast.success("✨ OMNIA AI"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setGenSeo(false); }
  };
  const [genList, setGenList] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [rendering, setRendering] = useState(false);
  const genRender = async () => {
    setRendering(true);
    try {
      const { data } = await api.post(`/aura/products/${id}/render/generate`, { lang });
      setP((prev) => ({ ...prev, cover: data.url, gallery: [...(prev.gallery || []), data.url] }));
      toast.success("✨ OMNIA AI");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error"); }
    finally { setRendering(false); }
  };
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setP((prev) => ({ ...prev, cover: url, gallery: [...(prev.gallery || []), url] }));
      toast.success(t("os.upload.btn"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Error"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const generateListings = async () => {
    setGenList(true);
    try { const { data } = await api.post(`/aura/products/${id}/listings/generate`, { lang }); setP((prev) => ({ ...prev, listings: data })); toast.success("✨ OMNIA AI"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setGenList(false); }
  };
  const totalStock = (p?.variants || []).reduce((s, v) => s + (Number(v.stock) || 0), 0);
  const TABS = ["details", "variants", "media", "seo", "social"];
  const toggleGallery = (url) => {
    const g = p.gallery || [];
    set("gallery", g.includes(url) ? g.filter((u) => u !== url) : [...g, url]);
  };

  return (
    <OSLayout title={t("os.nav.aura")}>
      <Link to="/os/aura" className="inline-flex items-center gap-2 text-sm text-navy/50 hover:text-navy transition-colors mb-6">
        <ArrowLeft className="w-4 h-4" /> {t("os.aura.back")}
      </Link>

      {!p ? (
        <div className="text-navy/40">{t("os.loading")}</div>
      ) : (
        <div className="space-y-6">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}
            className="flex flex-col md:flex-row gap-6 bg-white border border-navy/10 rounded-lg p-6">
            <div className="w-full md:w-40 h-40 rounded-lg overflow-hidden bg-cream shrink-0">
              <img src={mediaUrl(p.cover) || FALLBACK_IMG} alt={p.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 flex flex-col justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h2 className="font-editorial text-3xl tracking-tight">{p.name}</h2>
                  <span className="overline text-navy/40">{p.sku}</span>
                </div>
                <div className="text-navy/50 mt-1">{p.collection || p.category} · ${Number(p.price).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-3">
                <select data-testid="aura-status-select" value={p.status} onChange={(e) => set("status", e.target.value)}
                  className="bg-cream border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-violet">
                  <option value="draft">{t("os.aura.status.draft")}</option>
                  <option value="active">{t("os.aura.status.active")}</option>
                  <option value="archived">{t("os.aura.status.archived")}</option>
                </select>
                <button data-testid="aura-save" onClick={save}
                  className="rounded-full bg-navy text-white px-6 py-2.5 text-sm hover:bg-navy-soft transition-colors">{t("os.aura.save")}</button>
                <ItemMenu testId="aura-detail" onInvite={() => setShare(true)} onDelete={doDelete} />
              </div>
            </div>
          </motion.div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-navy/10 overflow-x-auto">
            {TABS.map((tk) => (
              <button key={tk} data-testid={`aura-tab-${tk}`} onClick={() => setTab(tk)}
                className={`px-4 py-3 text-sm -mb-px border-b-2 whitespace-nowrap transition-colors ${tab === tk ? "border-violet text-navy" : "border-transparent text-navy/50 hover:text-navy"}`}>
                {t(`os.aura.tabs.${tk}`)}
              </button>
            ))}
          </div>

          {/* Details */}
          {tab === "details" && (
            <div className="bg-white border border-navy/10 rounded-lg p-6 md:p-8 space-y-4" data-testid="aura-details-panel">
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="overline text-navy/50">{t("os.aura.fields.name")}</label>
                  <input data-testid="aura-field-name" value={p.name} onChange={(e) => set("name", e.target.value)} className={`mt-2 ${inputCls}`} /></div>
                <div><label className="overline text-navy/50">{t("os.aura.fields.sku")}</label>
                  <input value={p.sku} onChange={(e) => set("sku", e.target.value)} className={`mt-2 ${inputCls}`} /></div>
                <div><label className="overline text-navy/50">{t("os.aura.fields.price")}</label>
                  <input type="number" data-testid="aura-field-price" value={p.price} onChange={(e) => set("price", e.target.value)} className={`mt-2 ${inputCls}`} /></div>
                <div><label className="overline text-navy/50">{t("os.aura.fields.category")}</label>
                  <select value={p.category} onChange={(e) => set("category", e.target.value)} className={`mt-2 ${inputCls}`}>
                    <option>Objetos</option><option>Decoración</option><option>Iluminación</option><option>Textil</option><option>Mobiliario</option>
                  </select></div>
                <div><label className="overline text-navy/50">{t("os.aura.fields.collection")}</label>
                  <select value={p.collection || ""} onChange={(e) => set("collection", e.target.value)} className={`mt-2 ${inputCls}`}>
                    <option value="">—</option>
                    {collections.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select></div>
                <div><label className="overline text-navy/50">{t("os.aura.fields.materials")}</label>
                  <input data-testid="aura-field-materials" value={(p.materials || []).join(", ")} placeholder={t("os.aura.fields.materialsHint")}
                    onChange={(e) => set("materials", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={`mt-2 ${inputCls}`} /></div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="overline text-navy/50">{t("os.aura.fields.description")}</label>
                  <button data-testid="aura-ai-desc" onClick={generateDesc} disabled={genDesc} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-navy/15 px-3 py-1.5 hover:border-violet hover:text-violet transition-colors disabled:opacity-60">
                    {genDesc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {t("os.aura.aiDesc")}
                  </button>
                </div>
                <textarea data-testid="aura-field-description" rows={4} value={p.description || ""} onChange={(e) => set("description", e.target.value)} className={`mt-2 ${inputCls} resize-none`} />
              </div>
            </div>
          )}

          {/* Variants & inventory */}
          {tab === "variants" && (
            <div className="bg-white border border-navy/10 rounded-lg p-6 md:p-8" data-testid="aura-variants-panel">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-medium">{t("os.aura.variants.title")}</h3>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-navy/50">{t("os.aura.variants.totalStock")}: <b className="text-navy">{totalStock}</b></span>
                  <button data-testid="aura-variant-add" onClick={addVariant} className="inline-flex items-center gap-2 text-sm rounded-full border border-navy/15 px-4 py-2 hover:border-violet hover:text-violet transition-colors">
                    <Plus className="w-4 h-4" /> {t("os.aura.variants.add")}
                  </button>
                </div>
              </div>
              <div className="hidden sm:grid grid-cols-[1fr_1fr_120px_100px_40px] gap-3 overline text-navy/40 mb-2 px-1">
                <span>{t("os.aura.variants.name")}</span><span>{t("os.aura.variants.sku")}</span><span>{t("os.aura.variants.price")}</span><span>{t("os.aura.variants.stock")}</span><span />
              </div>
              <div className="space-y-2">
                {(p.variants || []).map((v, i) => (
                  <div key={v.id || i} data-testid={`aura-variant-${i}`} className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_120px_100px_40px] gap-3 items-center">
                    <input value={v.name} onChange={(e) => setVariant(i, "name", e.target.value)} className={inputCls} />
                    <input value={v.sku} onChange={(e) => setVariant(i, "sku", e.target.value)} className={inputCls} />
                    <input type="number" value={v.price} onChange={(e) => setVariant(i, "price", e.target.value)} className={inputCls} />
                    <input type="number" data-testid={`aura-variant-stock-${i}`} value={v.stock} onChange={(e) => setVariant(i, "stock", e.target.value)}
                      className={`${inputCls} ${Number(v.stock) < 10 ? "border-gold text-gold" : ""}`} />
                    <button onClick={() => removeVariant(i)} className="text-navy/30 hover:text-destructive justify-self-end"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Media / DAM */}
          {tab === "media" && (
            <div className="bg-white border border-navy/10 rounded-lg p-6 md:p-8 space-y-6" data-testid="aura-media-panel">
              <div>
                <div className="overline text-navy/50 mb-3">{t("os.aura.media.cover")}</div>
                <div className="flex items-end gap-4">
                  <div className="w-40 h-40 rounded-lg overflow-hidden bg-cream"><img src={mediaUrl(p.cover) || FALLBACK_IMG} alt="" className="w-full h-full object-cover" /></div>
                  <div>
                    <label data-testid="aura-upload-btn" className={`inline-flex items-center gap-2 text-sm rounded-full border border-navy/15 px-4 py-2 cursor-pointer hover:border-violet hover:text-violet transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {uploading ? t("os.upload.uploading") : t("os.upload.btn")}
                      <input type="file" accept="image/*" data-testid="aura-upload-input" className="hidden" onChange={onUpload} />
                    </label>
                    <button type="button" data-testid="aura-render-btn" onClick={genRender} disabled={rendering}
                      className="ml-2 inline-flex items-center gap-2 text-sm rounded-full bg-gradient-violet text-white px-4 py-2 hover:opacity-90 transition-opacity disabled:opacity-60">
                      {rendering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {rendering ? t("os.render.generating") : t("os.render.btn")}
                    </button>
                    <div className="text-xs text-navy/40 mt-2">{t("os.upload.hint")}</div>
                  </div>
                </div>
              </div>
              <div>
                <div className="overline text-navy/50 mb-3">{t("os.aura.media.pick")}</div>
                {lib && ["objects", "lifestyle", "ambient"].map((cat) => (
                  <div key={cat} className="mb-4">
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                      {lib[cat].map((url) => (
                        <div key={url} className="relative group aspect-square rounded-lg overflow-hidden bg-cream">
                          <img src={url} alt="" className={`w-full h-full object-cover ${(p.gallery || []).includes(url) ? "ring-2 ring-violet" : ""}`} />
                          <div className="absolute inset-0 bg-navy/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <button title={t("os.aura.media.setCover")} onClick={() => set("cover", url)} className="w-8 h-8 rounded-full bg-white flex items-center justify-center"><Star className="w-4 h-4 text-navy" /></button>
                            <button onClick={() => toggleGallery(url)} className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                              {(p.gallery || []).includes(url) ? <Check className="w-4 h-4 text-violet" /> : <Plus className="w-4 h-4 text-navy" />}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SEO */}
          {tab === "seo" && (
            <div className="bg-white border border-navy/10 rounded-lg p-6 md:p-8 space-y-4" data-testid="aura-seo-panel">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">{t("os.aura.seo.title")}</h3>
                <button data-testid="aura-ai-seo" onClick={generateSeo} disabled={genSeo} className="inline-flex items-center gap-1.5 text-sm rounded-full border border-navy/15 px-4 py-2 hover:border-violet hover:text-violet transition-colors disabled:opacity-60">
                  {genSeo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {t("os.aura.aiSeo")}
                </button>
              </div>
              <div><label className="overline text-navy/50">{t("os.aura.seo.metaTitle")}</label>
                <input data-testid="aura-seo-title" value={p.seo?.title || ""} onChange={(e) => setSeo("title", e.target.value)} className={`mt-2 ${inputCls}`} /></div>
              <div><label className="overline text-navy/50">{t("os.aura.seo.metaDesc")}</label>
                <textarea rows={2} value={p.seo?.description || ""} onChange={(e) => setSeo("description", e.target.value)} className={`mt-2 ${inputCls} resize-none`} /></div>
              <div><label className="overline text-navy/50">{t("os.aura.seo.keywords")}</label>
                <input value={p.seo?.keywords || ""} onChange={(e) => setSeo("keywords", e.target.value)} className={`mt-2 ${inputCls}`} /></div>
            </div>
          )}

          {/* Social & Marketplace listings */}
          {tab === "social" && (
            <div className="bg-white border border-navy/10 rounded-lg p-6 md:p-8 space-y-5" data-testid="aura-social-panel">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">{t("os.aura.listings.title")}</h3>
                <button data-testid="aura-ai-listings" onClick={generateListings} disabled={genList}
                  className="inline-flex items-center gap-1.5 text-sm rounded-full bg-gradient-violet text-white px-4 py-2 hover:opacity-90 transition-opacity disabled:opacity-60">
                  {genList ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {t("os.aura.listings.aiBtn")}
                </button>
              </div>
              {["instagram", "pinterest", "amazon"].map((ch) => (
                <div key={ch}>
                  <label className="overline text-navy/50">{t(`os.aura.listings.${ch}`)}</label>
                  <textarea data-testid={`aura-listing-${ch}`} rows={ch === "amazon" ? 5 : 3}
                    value={p.listings?.[ch] || ""}
                    onChange={(e) => setP({ ...p, listings: { ...(p.listings || {}), [ch]: e.target.value } })}
                    placeholder={t("os.aura.aiSoon")} className={`mt-2 ${inputCls} resize-none`} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {share && <ShareDialog collection="aura_products" id={id} title={p?.name} onClose={() => setShare(false)} />}
    </OSLayout>
  );
}
